import axios from 'axios';

/**
 * REACT_APP_API_BASE_URL used to default to production when unset. A fresh
 * checkout with no .env.local is exactly that unset state, so a local dev
 * build with no explicit environment choice silently talked to
 * api.customer.avrioai.com -- real customer data -- instead of failing to
 * start. That is how a session ended up running against production for a
 * full night without anyone choosing that.
 *
 * There is no safe default host to fall back to: every real build path
 * (`.env.production`, `.env.staging`, `.env.test`, and both deploy
 * workflows) already sets this explicitly, so the only way to reach this
 * branch is a build that was never told which environment it's for -- and
 * that build must refuse to start, not guess.
 */
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

if (!apiBaseUrl) {
  throw new Error(
    'REACT_APP_API_BASE_URL is not set. Refusing to start rather than ' +
      'silently talking to production -- set it in .env.local for local ' +
      'development (see .env.example).',
  );
}

const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 10000, // Optional: Set a timeout for requests
  // No tenant header here, deliberately. A hardcoded tenant id used to sit
  // inside this block, and the backend never read it: JwtAuthenticationFilter
  // reads Authorization and nothing else, while the same field name inside the
  // API is a JPA @Param bound from the authenticated user's own tenant, which
  // AdminUserManagementStrategy.createUser overwrites for any caller-supplied
  // value. So it selected nothing -- but it read exactly like a mechanism for
  // selecting one. Anyone building self-serve signup on top of this client
  // could reasonably infer that tenant choice is client-supplied. It is not,
  // and if it ever became so that would be the cross-tenant bug.
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Whether the caller already supplied its own credential.
 *
 * Two shapes reach this. At runtime axios has already normalised
 * `config.headers` into an AxiosHeaders instance, which matches header names
 * case-insensitively through `has`. A caller -- or a test -- can also hand the
 * interceptor a plain object, where the key's case is whatever was typed. Both
 * are checked, because a header this function fails to notice is a header it
 * silently overwrites.
 */
const hasCallerCredential = (headers: unknown): boolean => {
  if (!headers || typeof headers !== 'object') return false;

  const candidate = headers as {
    has?: (name: string) => boolean;
  } & Record<string, unknown>;

  if (typeof candidate.has === 'function') {
    return candidate.has('Authorization');
  }

  return Object.keys(candidate).some(
    (name) => name.toLowerCase() === 'authorization',
  );
};

/**
 * Requests that start a session, or ask for a reset because there is none. A
 * stored session token must never ride along on these.
 *
 * avrio-backend lists them as public, but its JwtAuthenticationFilter still
 * reads any Authorization header it is given and rejects the whole request
 * when that token is expired, before the endpoint runs. So the first sign-in
 * after a session lapsed always failed: the login went out carrying the
 * expired token, came back 401 ("ExpiredJwtException" on POST
 * /api/auth/login, staging, 16 Oct 2026), the 401 handler cleared the token,
 * and the second attempt worked. To the person signing in it looked like a
 * mistyped password.
 *
 * Matched on the path alone, query string ignored. Password reset itself
 * (/api/auth/reset-password) is deliberately not here: it carries its own
 * one-time token, which hasCallerCredential already leaves alone.
 */
const SESSION_START_PATHS: readonly RegExp[] = [
  /^\/api\/auth\/login(\/|$)/,
  /^\/api\/auth\/sso-login$/,
  /^\/api\/auth\/reset-password\/request$/,
];

/**
 * Public pages' requests: the self-serve signup flow, the page Stripe Checkout
 * returns to, and the outreach unsubscribe link. avrio-backend permits all of
 * them without a session and none reads the caller's identity, so a stored
 * session token has nothing to add -- but an expired one gets the request
 * rejected with a 401 before the endpoint runs, which the 401 handler below
 * turns into a redirect to the login page.
 *
 * Staging, 28 Oct 2026: /selfserve with an expired token in storage went
 * straight to /auth/login. The handler had cleared the token, so a reload
 * showed the signup page, which is why it looked like a one-off. It hits
 * anyone who has signed in in that browser before and let the session lapse.
 */
const PUBLIC_PATHS: readonly RegExp[] = [
  /^\/api\/self-serve\/config$/,
  /^\/api\/self-serve\/signup(\/progress)?$/,
  /^\/api\/outreach\/unsubscribe$/,
];

const sendsNoSession = (url: string | undefined): boolean => {
  if (!url) return false;
  // A relative URL is resolved against a placeholder origin only to read its
  // path; an absolute one keeps its own.
  const path = new URL(url, 'http://placeholder').pathname;
  return [...SESSION_START_PATHS, ...PUBLIC_PATHS].some((pattern) =>
    pattern.test(path),
  );
};

// Optionally, add request/response interceptors
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    // The stored session token is a default, not an override. Password reset
    // carries the one-time token from the emailed link in this header, and
    // overwriting it with the stale session token meant anyone who had ever
    // signed in in this browser could not complete a reset at all -- the
    // request went out authenticated as their old session instead of as the
    // reset it was.
    if (
      token &&
      !hasCallerCredential(config.headers) &&
      !sendsNoSession(config.url)
    ) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear token or handle unauthorized access
      localStorage.removeItem('token');

      if (window.location.pathname !== '/auth/login') {
        window.location.href = '/auth/login';
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
